
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FileText, Upload, Download, Eye, Trash2, AlertCircle,
  CheckCircle, Filter, Users, Calendar, ChevronDown, ChevronUp, X
} from 'lucide-react';
import * as documentService from '../../services/documentService';
import { Document } from '../../services/documentService';
import userService from '../../services/userService';
import { User } from '../../services/userService';

const DOCUMENT_TYPES = [
  { value: 'factura', label: 'Factura' },
  { value: 'informe_estadistico', label: 'Informe Estadístico' },
];

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const currentYear = new Date().getFullYear();
const AÑOS = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];

const AdminInvoicesView: React.FC = () => {
  // --- Clients ---
  const [clients, setClients] = useState<User[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);

  // --- Upload form ---
  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');
  const [docType, setDocType] = useState<string>('factura');
  const [uploadYear, setUploadYear] = useState<number>(currentYear);
  const [uploadMonth, setUploadMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Existing documents ---
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);

  // --- Filter panel ---
  const [filterClientId, setFilterClientId] = useState<number | ''>('');
  const [filterType, setFilterType] = useState<string>('todos');
  const [filterYear, setFilterYear] = useState<number | ''>('');
  const [filterMonth, setFilterMonth] = useState<string>('todos');

  // --- Expanded sections ---
  const [showUploadForm, setShowUploadForm] = useState(true);

  // --- Delete confirm ---
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadClients = async () => {
    setLoadingClients(true);
    setClientsError(null);
    try {
      const res = await userService.getAllUsers();
      setClients(res.users.filter(u => u.role === 'USER' && u.active !== false));
    } catch (err) {
      setClientsError(err instanceof Error ? err.message : 'Error al cargar la lista de clientes.');
    } finally {
      setLoadingClients(false);
    }
  };

  const loadData = async () => {
    setLoadingClients(true);
    setLoadingDocs(true);
    setClientsError(null);
    setDocsError(null);
    try {
      const clientsRes = await userService.getAllUsers();
      const activeClients = clientsRes.users.filter(u => u.role === 'USER' && u.active !== false);
      setClients(activeClients);
      setLoadingClients(false);
      loadDocumentsForClients(activeClients);
    } catch (err) {
      setClientsError(err instanceof Error ? err.message : 'Error al cargar la lista de clientes.');
      setLoadingClients(false);
      setLoadingDocs(false);
    }
  };

  const loadDocumentsForClients = async (activeClients: User[]) => {
    try {
      const perClientFetches = activeClients.map(c =>
        Promise.all([
          documentService.getDocumentsByType(c.id, 'factura'),
          documentService.getDocumentsByType(c.id, 'informe_estadistico'),
        ]).then(([f, i]) => [...f.documents, ...i.documents])
      );

      const results = await Promise.allSettled(perClientFetches);
      const all: Document[] = results
        .filter((r): r is PromiseFulfilledResult<Document[]> => r.status === 'fulfilled')
        .flatMap(r => r.value)
        .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

      setDocuments(all);
    } catch {
      setDocsError('Error al cargar documentos. Intenta de nuevo.');
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (!selectedClientId) {
      setUploadError('Selecciona un cliente.');
      return;
    }
    if (!selectedFile) {
      setUploadError('Selecciona un archivo.');
      return;
    }
    if (!documentService.isValidFileType(selectedFile)) {
      setUploadError('Solo se permiten archivos PDF, JPG o PNG.');
      return;
    }
    if (!documentService.isValidFileSize(selectedFile)) {
      setUploadError('El archivo no puede superar los 10 MB.');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      await documentService.uploadDocument(
        selectedFile,
        selectedClientId as number,
        docType,
        uploadYear,
        uploadMonth
      );

      const client = clients.find(c => c.id === selectedClientId);
      const docLabel = DOCUMENT_TYPES.find(t => t.value === docType)?.label ?? docType;
      setUploadSuccess(
        `${docLabel} subida correctamente para ${client?.companyName ?? client?.username}.`
      );

      // Reset file input
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Reload documents
      await loadDocumentsForClients(clients);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Error al subir el archivo. Intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (doc: Document) => {
    try {
      await documentService.viewDocument(doc.id);
    } catch {
      setDocsError('Error al visualizar el documento.');
      setTimeout(() => setDocsError(null), 5000);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      await documentService.downloadDocument(doc.id, doc.originalFileName);
    } catch {
      setDocsError('Error al descargar el documento.');
      setTimeout(() => setDocsError(null), 5000);
    }
  };

  const handleDelete = async (docId: number) => {
    setDeletingId(docId);
    setDeleteError(null);
    try {
      await documentService.deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Error al eliminar el documento.');
      setTimeout(() => setDeleteError(null), 5000);
    } finally {
      setDeletingId(null);
    }
  };

  const getClientName = (userId: number) => {
    const c = clients.find(cl => cl.id === userId);
    return c?.companyName ?? c?.username ?? `Cliente #${userId}`;
  };

  const getTipoBadge = (type: string) => {
    if (type === 'factura') {
      return (
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
          Factura
        </span>
      );
    }
    if (type === 'informe_estadistico') {
      return (
        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap">
          Informe Estadístico
        </span>
      );
    }
    return (
      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">
        {type}
      </span>
    );
  };

  const filteredDocs = useMemo(() => documents.filter(d => {
    if (filterClientId !== '' && d.userId !== filterClientId) return false;
    if (filterType !== 'todos' && d.documentType !== filterType) return false;
    if (filterYear !== '' && d.year !== filterYear) return false;
    if (filterMonth !== 'todos') {
      const idx = MESES.indexOf(filterMonth) + 1;
      if (d.month !== idx) return false;
    }
    return true;
  }), [documents, filterClientId, filterType, filterYear, filterMonth]);

  const { totalFacturas, totalInformes } = useMemo(() => ({
    totalFacturas: filteredDocs.filter(d => d.documentType === 'factura').length,
    totalInformes: filteredDocs.filter(d => d.documentType === 'informe_estadistico').length,
  }), [filteredDocs]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
        <FileText className="text-red-600" size={28} />
        Gestión de Facturas e Informes
      </h2>

      {/* ── Upload form ── */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <button
          onClick={() => setShowUploadForm(v => !v)}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition rounded-lg"
        >
          <div className="flex items-center gap-2 font-semibold text-gray-800">
            <Upload size={20} className="text-red-600" />
            Subir nuevo documento
          </div>
          {showUploadForm ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {showUploadForm && (
          <div className="px-6 pb-6 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* Client selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Users size={14} className="inline mr-1" />
                  Cliente
                </label>
                <select
                  value={selectedClientId}
                  onChange={e => setSelectedClientId(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                  disabled={loadingClients}
                >
                  <option value="">Selecciona un cliente...</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.companyName ? `${c.companyName} (${c.username})` : c.username}
                    </option>
                  ))}
                </select>
                {clientsError && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-red-600">
                    <AlertCircle size={13} />
                    <span>{clientsError}</span>
                    <button
                      onClick={loadClients}
                      className="underline hover:text-red-800 font-medium"
                    >
                      Reintentar
                    </button>
                  </div>
                )}
              </div>

              {/* Document type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de documento</label>
                <select
                  value={docType}
                  onChange={e => setDocType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                >
                  {DOCUMENT_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar size={14} className="inline mr-1" />
                  Año
                </label>
                <select
                  value={uploadYear}
                  onChange={e => setUploadYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                >
                  {AÑOS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              {/* Month */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
                <select
                  value={uploadMonth}
                  onChange={e => setUploadMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                >
                  {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
            </div>

            {/* File picker */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Archivo (PDF, JPG, PNG — máx. 10 MB)</label>
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 cursor-pointer border border-gray-300 rounded-lg"
                />
                {selectedFile && (
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-gray-400 hover:text-gray-600 transition"
                    title="Quitar archivo"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              {selectedFile && (
                <p className="mt-1 text-xs text-gray-500">
                  {selectedFile.name} — {documentService.formatFileSize(selectedFile.size)}
                </p>
              )}
            </div>

            {/* Feedback messages */}
            {uploadError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-300 text-red-700 rounded-lg flex items-center gap-2 text-sm">
                <AlertCircle size={16} />
                {uploadError}
              </div>
            )}
            {uploadSuccess && (
              <div className="mt-3 p-3 bg-green-50 border border-green-300 text-green-700 rounded-lg flex items-center gap-2 text-sm">
                <CheckCircle size={16} />
                {uploadSuccess}
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={uploading || !selectedFile || selectedClientId === ''}
              className="mt-4 flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Subir documento
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Total documentos</p>
          <p className="text-3xl font-bold text-gray-800">{filteredDocs.length}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-gray-600 mb-1">Facturas</p>
          <p className="text-3xl font-bold text-blue-800">{totalFacturas}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
          <p className="text-sm text-gray-600 mb-1">Informes Estadísticos</p>
          <p className="text-3xl font-bold text-purple-800">{totalInformes}</p>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={18} className="text-gray-600" />
          <span className="font-semibold text-gray-800">Filtros</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cliente</label>
            <select
              value={filterClientId}
              onChange={e => setFilterClientId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">Todos los clientes</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.companyName ?? c.username}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="todos">Todos</option>
              {DOCUMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Año</label>
            <select
              value={filterYear}
              onChange={e => setFilterYear(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">Todos</option>
              {AÑOS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Mes</label>
            <select
              value={filterMonth}
              onChange={e => setFilterMonth(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="todos">Todos</option>
              {MESES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Documents table ── */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">
            Documentos subidos ({filteredDocs.length})
          </h3>
        </div>

        {(docsError || deleteError) && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-300 text-red-700 rounded-lg flex items-center gap-2 text-sm">
            <AlertCircle size={16} />
            {docsError ?? deleteError}
          </div>
        )}

        {loadingDocs ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Cliente', 'Tipo', 'Período', 'Archivo', 'Tamaño', 'Subido', 'Acciones'].map(col => (
                    <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 animate-pulse">
                {[...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-32" /></td>
                    <td className="px-4 py-3"><div className="h-6 bg-gray-200 rounded-full w-24" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-36" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                    <td className="px-4 py-3"><div className="h-6 bg-gray-200 rounded w-16" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <FileText size={44} className="mx-auto mb-3 opacity-40" />
            <p>No hay documentos para los filtros seleccionados.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Período</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Archivo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Tamaño</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Subido</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredDocs.map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {getClientName(doc.userId)}
                    </td>
                    <td className="px-4 py-3">
                      {getTipoBadge(doc.documentType)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                      {documentService.formatPeriod(doc.year, doc.month)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <FileText size={15} className="text-blue-500 flex-shrink-0" />
                        <span className="text-sm text-gray-800 truncate max-w-[180px]" title={doc.originalFileName}>
                          {doc.originalFileName}
                        </span>
                        <span className="text-xs text-gray-400">v{doc.version}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {documentService.formatFileSize(doc.fileSize)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {new Date(doc.uploadedAt).toLocaleDateString('es-CL', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleView(doc)}
                          className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition"
                          title="Ver"
                        >
                          <Eye size={17} />
                        </button>
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition"
                          title="Descargar"
                        >
                          <Download size={17} />
                        </button>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          disabled={deletingId === doc.id}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition disabled:opacity-40"
                          title="Eliminar"
                        >
                          {deletingId === doc.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
                          ) : (
                            <Trash2 size={17} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvoicesView;
